import { useEffect, useState } from "react"
import { addDays, format, parseISO } from "date-fns"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import DatePicker from "~/components/DatePicker"
import { fetchProperties } from "~/apis/properties"
import { fetchBooking, createBooking, updateBooking } from "~/apis/bookings"
import { extractDetails } from "~/lib/extractDetails"
import routes from "~/routes"

const FIELD_LABELS = {
  guest_name: "name",
  guest_phone: "phone",
  guest_count: "guest count",
  starts_on: "check-in",
  ends_on: "check-out",
}

const SOURCE_LABELS = {
  direct: "Manual / direct",
  riparide: "Riparide",
  airbnb: "Airbnb",
}

// Paste a booking email / SMS thread and pull the details out of it. Pure
// pattern matching in the browser -- instant, offline, no account needed.
function PasteExtractor({ datesLocked, onExtracted }) {
  const [ open, setOpen ] = useState(false)
  const [ text, setText ] = useState("")
  const [ result, setResult ] = useState(null)

  function run() {
    const found = extractDetails(text)
    if (datesLocked) {
      delete found.starts_on
      delete found.ends_on
    }
    onExtracted(found)
    setResult(Object.keys(found).map((key) => FIELD_LABELS[key]))
  }

  return (
    <div className="mb-4 rounded-xl border border-border bg-muted/50">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-foreground"
      >
        Fill from pasted text
        <span className="ml-auto text-muted-foreground">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4">
          <Textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={5}
            placeholder="Paste the booking email, Riparide confirmation, or a text from the guest. The phone number, dates and guest count get picked out automatically."
          />
          <div className="mt-2 flex items-center gap-3">
            <Button type="button" size="sm" onClick={run} disabled={!text.trim()}>
              Fill the form
            </Button>
            {result?.length > 0 && (
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                Found {result.filter(Boolean).join(", ")}. Check it over, then save.
              </span>
            )}
            {result?.length === 0 && (
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                Couldn't spot a phone number or dates in that — fill them in below.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const KIND_OPTIONS = [
  { value: "reservation", label: "Booking", hint: "A paying stay." },
  { value: "residency", label: "Residency", hint: "One of the monthly artist stays — tracked on the Residencies tab. Booked direct." },
  { value: "block", label: "Block", hint: "No guests — keeps the property off the calendar for maintenance or your own use. No clean or messages." },
]

const emptyForm = {
  property_id: "",
  source: "direct",
  kind: "reservation",
  starts_on: "",
  ends_on: "",
  guest_name: "",
  guest_phone: "",
  guest_email: "",
  guest_count: "",
  notes: "",
  flags: {},
}

export default function BookingForm() {
  const { id } = useParams()
  const [ search ] = useSearchParams()
  const navigate = useNavigate()
  const [ properties, setProperties ] = useState([])
  const [ form, setForm ] = useState({ ...emptyForm, starts_on: search.get("date") || "", kind: search.get("kind") || "reservation" })
  const [ synced, setSynced ] = useState(false)
  const [ sourceLabel, setSourceLabel ] = useState(null)
  // Blocks have no checkout, so "end date" is ambiguous -- this toggles whether
  // the picked end date is the last blocked day (inclusive) or the day the
  // block lifts (exclusive, same convention as a reservation's checkout).
  const [ endInclusive, setEndInclusive ] = useState(false)
  const [ error, setError ] = useState(null)
  const [ busy, setBusy ] = useState(false)

  useEffect(() => {
    fetchProperties().then((props) => {
      setProperties(props)
      setForm((f) => ({ ...f, property_id: f.property_id || search.get("property_id") || props[0]?.id }))
    })
    if (id) {
      fetchBooking(id).then((b) => {
        setForm({
          property_id: b.property_id,
          source: b.source,
          kind: b.kind || "reservation",
          starts_on: b.starts_on,
          ends_on: b.ends_on,
          guest_name: b.guest_name || "",
          guest_phone: b.guest_phone || "",
          guest_email: b.guest_email || "",
          guest_count: b.guest_count || "",
          notes: b.notes || "",
          flags: b.flags || {},
        })
        setSynced(b.synced)
        setSourceLabel(SOURCE_LABELS[b.source] || b.source)
        setEndInclusive(false)
      })
    }
  }, [ id ])

  const property = properties.find((p) => String(p.id) === String(form.property_id))
  const flagDefinitions = property?.flag_schema || []
  const isBlock = form.kind === "block"
  const isResidency = form.kind === "residency"

  function set(key) {
    return (event) => setForm({ ...form, [key]: event.target.value })
  }

  function setField(key, value) {
    setForm({ ...form, [key]: value })
  }

  function toggleFlag(key) {
    setForm((f) => ({ ...f, flags: { ...f.flags, [key]: !f.flags[key] } }))
  }

  function applyExtracted(fields) {
    setForm((f) => ({ ...f, ...fields }))
  }

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const payload = {
      ...form,
      guest_count: form.guest_count === "" ? null : Number(form.guest_count),
      flags: Object.fromEntries(Object.entries(form.flags).filter(([ , on ]) => on)),
    }
    if (isBlock) {
      payload.guest_name = ""
      payload.guest_phone = ""
      payload.guest_count = null
      payload.flags = {}
      // Storage is always exclusive of ends_on (same as a reservation's
      // checkout) -- if the end date picked was meant to be the last blocked
      // day itself, push it out by one so that day is actually covered.
      if (endInclusive && form.ends_on) payload.ends_on = format(addDays(parseISO(form.ends_on), 1), "yyyy-MM-dd")
    }

    try {
      const saved = id ? await updateBooking(id, payload) : await createBooking(payload)
      navigate(routes.bookingDetail(saved.id), { replace: true })
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-lg px-6 py-12">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{id ? (synced ? "Edit guest details" : isResidency ? "Edit residency" : "Edit booking") : isResidency ? "New residency" : "New booking"}</CardTitle>
        </CardHeader>
        <CardContent>
          {error && <p className="mb-4 text-sm font-medium text-destructive">{error}</p>}

          <form onSubmit={submit}>
            {!isBlock && <PasteExtractor datesLocked={synced} onExtracted={applyExtracted} />}

            <div className="mb-3 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Property</Label>
                <Select value={String(form.property_id || "")} onValueChange={(value) => setForm({ ...form, property_id: value, flags: {} })} disabled={!!id}>
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!isBlock && !isResidency && (
                <div className="col-span-2">
                  <Label>Booked through</Label>
                  <Select value={form.source} onValueChange={(value) => setField("source", value)}>
                    <SelectTrigger className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SOURCE_LABELS).map(([ value, label ]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="starts_on">Check-in</Label>
                <div className="mt-1">
                  <DatePicker
                    id="starts_on"
                    value={form.starts_on}
                    onChange={(value) => setField("starts_on", value)}
                    disabled={synced}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="ends_on">{isBlock ? "End date" : "Check-out"}</Label>
                <div className="mt-1">
                  <DatePicker
                    id="ends_on"
                    value={form.ends_on}
                    onChange={(value) => setField("ends_on", value)}
                    disabled={synced}
                    min={form.starts_on}
                  />
                </div>
              </div>

              {isBlock && (
                <div className="col-span-2 -mt-1">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={endInclusive}
                      onChange={(event) => setEndInclusive(event.target.checked)}
                      className="h-4 w-4"
                    />
                    End date is inclusive (the property is blocked on that day too)
                  </label>
                </div>
              )}
            </div>
            {synced && (
              <p className="-mt-1 mb-3 text-xs text-muted-foreground">
                Dates come from the {sourceLabel} calendar feed and can't be edited here. Everything else is yours to fill in — including marking a "Not available" hold as the booking it really is.
              </p>
            )}

            <div role="radiogroup" className="mb-4 grid gap-2">
              {KIND_OPTIONS.map((option) => (
                <label key={option.value} className={`flex items-start gap-2 rounded-lg border p-2.5 text-sm ${form.kind === option.value ? "border-brand bg-brand/10" : "border-border"}`}>
                  <input type="radio" name="booking-kind" value={option.value} checked={form.kind === option.value} onChange={() => setField("kind", option.value)} className="mt-0.5 h-4 w-4" />
                  <span>
                    <span className="font-medium text-foreground">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">
                      {synced && option.value === "block" ? "Tick if these feed dates are just a hold with no guests." : option.hint}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            {!isBlock && (
              <>
                <div className="mb-3 grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="guest_name">{isResidency ? "Resident name" : "Guest name"}</Label>
                    <Input id="guest_name" value={form.guest_name} onChange={set("guest_name")} placeholder="First name is what messages use" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="guest_phone">Phone</Label>
                    <Input id="guest_phone" type="tel" value={form.guest_phone} onChange={set("guest_phone")} placeholder="04…" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="guest_count">{isResidency ? "People" : "Guests"}</Label>
                    <Input id="guest_count" type="number" min="1" value={form.guest_count} onChange={set("guest_count")} className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="guest_email">Email (optional)</Label>
                    <Input id="guest_email" type="email" value={form.guest_email} onChange={set("guest_email")} className="mt-1" />
                  </div>
                </div>

                {flagDefinitions.length > 0 && (
                  <fieldset className="mb-3 space-y-2">
                    <legend className="text-sm font-medium text-foreground">For the cleaner</legend>
                    {flagDefinitions.map((definition) => (
                      <label key={definition.key} className="flex items-start gap-2 text-sm">
                        <input type="checkbox" checked={!!form.flags[definition.key]} onChange={() => toggleFlag(definition.key)} className="mt-0.5 h-4 w-4" />
                        <span>
                          <span className="text-foreground">{definition.label}</span>
                          {definition.hint && <span className="block text-xs text-muted-foreground">{definition.hint}</span>}
                        </span>
                      </label>
                    ))}
                  </fieldset>
                )}
              </>
            )}

            <div className="mb-5">
              <Label htmlFor="notes">{isResidency ? "Details (practice, project, anything to remember)" : "Notes"}</Label>
              <Textarea id="notes" rows={2} value={form.notes} onChange={set("notes")} className="mt-1" />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="flex-1">
                {busy ? "Saving…" : isBlock ? "Save block" : isResidency ? "Save residency" : "Save booking"}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
